/**
 * Browser Recording API Service
 *
 * API client functions for browser-based audio recording sessions.
 * Handles session management, chunk upload URL generation, and recording completion.
 */

import { apiClient } from "./api";
import type {
  BrowserRecordingSession,
  ChunkUploadUrl,
  StartBrowserRecordingRequest,
  GetChunkUploadUrlRequest,
  CompleteBrowserRecordingRequest,
  CompleteBrowserRecordingResponse,
} from "@/types/browser-recording";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Start a new browser recording session
 *
 * Creates a session with S3 prefix for chunk uploads.
 */
export async function startBrowserRecordingSession(
  data: StartBrowserRecordingRequest
): Promise<BrowserRecordingSession> {
  const response = await apiClient.post(
    `${API_PREFIX}/recordings/browser/start`,
    data
  );
  return response.data;
}

/**
 * Get presigned URL for uploading a chunk
 *
 * Returns a presigned S3 URL for direct chunk upload.
 */
export async function getChunkUploadUrl(
  sessionId: string,
  data: GetChunkUploadUrlRequest
): Promise<ChunkUploadUrl> {
  const response = await apiClient.post(
    `${API_PREFIX}/recordings/browser/${sessionId}/chunk-url`,
    data
  );
  return response.data;
}

/**
 * Upload a chunk directly to S3 using presigned URL
 *
 * @returns true if upload successful, false otherwise
 */
export async function uploadChunkToS3(
  uploadUrl: string,
  chunk: Blob
): Promise<boolean> {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: chunk,
      headers: {
        "Content-Type": chunk.type,
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Complete browser recording session
 *
 * Finalizes the recording, concatenates chunks, creates reflection and recording records,
 * and triggers transcription processing.
 */
export async function completeBrowserRecording(
  sessionId: string,
  data: CompleteBrowserRecordingRequest
): Promise<CompleteBrowserRecordingResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/recordings/browser/${sessionId}/complete`,
    {
      total_chunks: data.total_chunks,
      total_duration_seconds: data.total_duration_seconds,
      title: data.title,
      description: data.description,
      user_context: data.user_context,
      therapy_style_id: data.therapy_style_id,
      expected_speakers: data.expected_speakers,
    }
  );
  return response.data;
}

/**
 * Abort/cancel a browser recording session
 *
 * Cleans up any uploaded chunks and removes the session.
 */
export async function abortBrowserRecording(sessionId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/recordings/browser/${sessionId}/abort`);
}

/**
 * Get status of a browser recording session
 */
export async function getBrowserRecordingStatus(sessionId: string): Promise<{
  session_id: string;
  chunks_uploaded: number;
  status: "active" | "completed" | "aborted";
}> {
  const response = await apiClient.get(
    `${API_PREFIX}/recordings/browser/${sessionId}/status`
  );
  return response.data;
}
